import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { SubHubGrid, ContentSection, FaqBlock, buildFaqLd, buildBreadcrumbLd, ResultCard, ReservedResultsArea, inputCls } from "@/components/age-calculator/shared";
import { TripleDateInput, AnalyzeButton, AnalyzingOverlay, WhatsAppShareButton, useAnalyzeGate } from "@/components/age-calculator/premium";

const TITLE = "Retirement Age Calculator: State Pension & Eligibility Finder";
const DESC = "Plan your future. Calculate your official retirement age, state pension eligibility matrix, and social security milestones instantly.";

const FAQS = [
  { q: "What is the state pension age in the UK?", a: "For anyone born after April 1960, the UK State Pension Age is 66, scheduled to rise to 67 between 2026 and 2028, and to 68 between 2044 and 2046." },
  { q: "What is the US Social Security Full Retirement Age (FRA)?", a: "For people born in 1960 or later, the US Social Security FRA is 67. Reduced benefits are available from age 62; delaying past FRA up to 70 increases monthly benefits by ~8% per year." },
  { q: "What is an RMD milestone?", a: "In the US, Required Minimum Distributions from traditional IRAs and 401(k)s currently begin at age 73 (rising to 75 in 2033). Missing an RMD triggers a steep IRS penalty." },
  { q: "Can I retire earlier than the state pension age?", a: "Yes, but you'll need private savings or an employer pension to bridge the gap. Use this calculator to see exactly how many years and months separate your planned retirement date from official state eligibility." },
];

const COUNTRY_AGE: Record<string, number> = { general: 65, uk: 66, us: 67 };

export const Route = createFileRoute("/age-calculator/retirement-pension")({
  head: () => leafHead({ path: "/age-calculator/retirement-pension", title: TITLE, description: DESC, scripts: [buildFaqLd(FAQS), buildBreadcrumbLd([{ name: "Home", url: "https://drhint.com/" }, { name: "Age Calculator", url: "https://drhint.com/age-calculator" }, { name: "Retirement & Pension", url: "https://drhint.com/age-calculator/retirement-pension" }])] }),
  component: Page,
});

function Page() {
  const [dob, setDob] = useState("");
  const [plannedAge, setPlannedAge] = useState(60);
  const [country, setCountry] = useState<keyof typeof COUNTRY_AGE>("general");
  const { analyzing, revealed, trigger, reset } = useAnalyzeGate();
  const canGo = !!dob;

  const r = useMemo(() => {
    if (!dob) return null;
    const b = new Date(dob);
    if (isNaN(b.getTime())) return null;
    const stateAge = COUNTRY_AGE[country];
    const planned = new Date(b); planned.setFullYear(b.getFullYear() + plannedAge);
    const stateDate = new Date(b); stateDate.setFullYear(b.getFullYear() + stateAge);
    const now = new Date();
    const yearsToPlanned = ((planned.getTime() - now.getTime()) / (365.25 * 86400000));
    const yearsToState = ((stateDate.getTime() - now.getTime()) / (365.25 * 86400000));
    const rmd = new Date(b); rmd.setFullYear(b.getFullYear() + 73);
    const earlyDate = new Date(b); earlyDate.setFullYear(b.getFullYear() + 62);
    const maxDate = new Date(b); maxDate.setFullYear(b.getFullYear() + 70);
    // Milestones list for timeline viz
    const milestones = [
      { label: "Early (62)", date: earlyDate, color: "bg-amber-400" },
      { label: `Your Plan (${plannedAge})`, date: planned, color: "bg-primary" },
      { label: `State (${stateAge})`, date: stateDate, color: "bg-emerald-500" },
      { label: "Max Benefits (70)", date: maxDate, color: "bg-indigo-500" },
      { label: "RMD (73)", date: rmd, color: "bg-rose-500" },
    ];
    const minY = b.getFullYear();
    const maxY = rmd.getFullYear();
    const span = Math.max(1, maxY - minY);
    return {
      plannedDate: planned.toISOString().slice(0, 7),
      stateDate: stateDate.toISOString().slice(0, 7),
      rmdDate: rmd.toISOString().slice(0, 7),
      yearsToPlanned: Math.max(0, yearsToPlanned).toFixed(1),
      yearsToState: Math.max(0, yearsToState).toFixed(1),
      stateAge,
      milestones, minY, maxY, span, nowYear: now.getFullYear(),
    };
  }, [dob, plannedAge, country]);

  const shareMsg = r ? `🏖️ Retirement Plan\n\n📅 Planned retirement: ${r.plannedDate} (${r.yearsToPlanned} yrs to go)\n💼 State pension: ${r.stateDate}\n💰 RMD milestone: ${r.rmdDate}\n\nPlan yours on https://drhint.com/age-calculator/retirement-pension` : "";

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/age-calculator" className="hover:text-primary">Age Calculator</Link> / Retirement & Pension
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{TITLE}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">{DESC}</p>
      </section>
      <section className="mx-auto max-w-5xl px-4">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
          <div className="grid gap-5 sm:grid-cols-3">
            <TripleDateInput label="Date of Birth" value={dob} onChange={(v) => { setDob(v); reset(); }} />
            <label className="flex flex-col gap-2" style={{ minHeight: 92 }}>
              <span className="text-sm font-semibold">Planned Retirement Age</span>
              <input type="number" min={40} max={80} value={plannedAge} onChange={(e) => { setPlannedAge(parseInt(e.target.value) || 60); reset(); }} className={inputCls()} />
            </label>
            <label className="flex flex-col gap-2" style={{ minHeight: 92 }}>
              <span className="text-sm font-semibold">Country</span>
              <select value={country} onChange={(e) => { setCountry(e.target.value as keyof typeof COUNTRY_AGE); reset(); }} className={inputCls()}>
                <option value="general">General (65)</option>
                <option value="uk">United Kingdom (66)</option>
                <option value="us">United States FRA (67)</option>
              </select>
            </label>
          </div>
          <AnalyzeButton onClick={trigger} disabled={!canGo || analyzing} label="Calculate Retirement Plan" />
          <AnalyzingOverlay visible={analyzing} />
          <ReservedResultsArea minHeight={280}>
            {revealed && r ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <ResultCard label="Planned Retirement" value={r.plannedDate} sub={`${r.yearsToPlanned} yrs from now`} />
                  <ResultCard label={`State Pension (age ${r.stateAge})`} value={r.stateDate} sub={`${r.yearsToState} yrs from now`} />
                  <ResultCard label="RMD Milestone (age 73)" value={r.rmdDate} sub="US IRA / 401(k) withdrawals" />
                  <ResultCard label="Bridge Years" value={Math.max(0, r.stateAge - plannedAge)} sub="Between plan and state" />
                </div>
                <div className="mt-6 rounded-2xl border border-border bg-card p-5">
                  <div className="text-sm font-semibold mb-4">Retirement Milestones Timeline</div>
                  <div className="relative h-2 rounded-full bg-muted">
                    {(() => {
                      const nowPct = Math.max(0, Math.min(1, (r.nowYear - r.minY) / r.span)) * 100;
                      return <div className="absolute top-0 h-full bg-primary/30 rounded-full" style={{ left: 0, width: `${nowPct}%` }} />;
                    })()}
                    {r.milestones.map((m) => {
                      const pct = Math.max(0, Math.min(1, (m.date.getFullYear() - r.minY) / r.span)) * 100;
                      return (
                        <div key={m.label} className="absolute -top-1.5 flex flex-col items-center" style={{ left: `${pct}%`, transform: "translateX(-50%)" }}>
                          <div className={`h-5 w-5 rounded-full border-2 border-background shadow ${m.color}`} />
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-2 text-[11px]">
                    {r.milestones.map((m) => (
                      <div key={m.label} className="flex items-center gap-1.5">
                        <span className={`inline-block h-2.5 w-2.5 rounded-full ${m.color}`} />
                        <span className="font-medium">{m.label}</span>
                        <span className="text-muted-foreground ml-auto tabular-nums">{m.date.getFullYear()}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <WhatsAppShareButton message={shareMsg} label="Share Retirement Plan" />
              </>
            ) : !analyzing ? (
              <div className="mt-6 rounded-2xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground">Enter your date of birth and tap Calculate.</div>
            ) : null}
          </ReservedResultsArea>
        </div>
      </section>
      <ContentSection title="Planning around state pension eligibility">
        <p>
          Every retirement plan hinges on one number: <strong>the year your state pension kicks in</strong>. For most
          people it's the difference between a comfortable retirement and a stressful one. The UK moved its State
          Pension Age to 66 in 2020 with statutory rises to 67 by 2028 and 68 in the 2040s. The US Full Retirement Age
          for anyone born from 1960 onward is 67, with reduced benefits from 62 and 8%-per-year credits for delaying up
          to 70.
        </p>
        <p>
          If you plan to retire earlier than your state pension age, this calculator shows the <em>bridge years</em> you'll
          need to fund yourself. If you plan to work longer, you'll see exactly how many additional years of accrual
          you're adding.
        </p>
        <h3>RMD milestones matter too</h3>
        <p>
          US retirees with traditional IRAs or 401(k)s must begin <strong>Required Minimum Distributions at age 73</strong>
          (rising to 75 in 2033 under SECURE 2.0). Missing an RMD triggers a 25% excise tax on the shortfall — the
          milestone card above puts that date on your calendar.
        </p>
        <p>
          To calculate exact age differentials between planning dates, use the{" "}
          <Link to="/age-calculator/chronological-date-difference" className="text-primary underline">Chronological & Date Difference Calculator</Link>,
          or return to the <Link to="/age-calculator" className="text-primary underline">main hub</Link> for our full toolset.
        </p>
      </ContentSection>
      <FaqBlock faqs={FAQS} />
      <SubHubGrid excludePath="/age-calculator/retirement-pension" />
    </Layout>
  );
}